import type * as ngRouter from '@angular/router'
import * as ents from '@attain-apps/entities'
import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import { QueryParam } from '../../../../query-param'
import { RouteParam } from '../../../../route-param'
import type * as routeBound from '../../../route-bound'
import * as variableOverrides from '../../../variable-overrides'
import type * as me from '../../feature'

import type { Feature } from './feature'
import * as models from './models'

export class Selectors {
  /** All currently available tenants */
  public readonly availableTenants
  /** Current tenant */
  public readonly tenant
  /** Alias identifying the current tenant */
  public readonly tenantAlias
  /** ID for the current tenant */
  public readonly tenantId
  /** Full context for the current tenant */
  public readonly tenantContext: store.selectors.BoundSelector<
    ents.core.admin.Context.ForTenant | undefined
  >

  /** All currently available zones */
  public readonly availableZones
  /** Current zone */
  public readonly zone
  /** Alias identifying the current zone */
  public readonly zoneAlias
  /** ID for the current zone */
  public readonly zoneId
  /** Full context for the current zone */
  public readonly zoneContext: store.selectors.BoundSelector<
    ents.core.admin.Context.ForZone | undefined
  >

  /** Apps for the current zone; empty until success, on error, or without a zone. */
  public readonly availableApps
  /**
   * Current app from root navigation context.
   *
   * This is useful for UI and navigation derivations. Route-bound app state
   * should be route-provided through {@link routeBound.Registry} so its
   * lifetime follows the app route injector.
   */
  public readonly app
  /** Alias identifying the current app */
  public readonly appAlias
  /** ID for the current app */
  public readonly appId
  /**
   * Full context for the current app from root navigation context.
   *
   * This selector does not own route-bound state lifetime. Collections/features
   * owned by an app route should be provided through
   * {@link routeBound.Registry}.
   */
  public readonly appContext: store.selectors.BoundSelector<
    ents.core.admin.Context.ForApp | undefined
  >

  /** Current appAll currently availables */
  public readonly availableAppPages
  /** Current app-page */
  public readonly appPage
  /** Alias identifying the current app-page */
  public readonly appPageAlias
  /** ID for the current app-page */
  public readonly appPageId
  /** Full context for the current app-page */
  public readonly pageId

  /** All currently available scenarios */
  public readonly availableScenarios
  /** Current scenario */
  public readonly scenario
  /** Alias identifying the current scenario */
  public readonly scenarioAlias
  /** ID for the current scenario */
  public readonly scenarioId
  /**
   * Full context for the current scenario from root navigation context.
   *
   * Route-bound scenario collections/features should not use this selector
   * directly for `context$`. Inject
   * {@link routeBound.scenario.Context.TOKEN} instead, so stale scenario
   * context from a previous app cannot feed the newly active app route.
   */
  public readonly scenarioContext: store.selectors.BoundSelector<
    ents.core.admin.Context.ForScenario | undefined
  >

  /** The most specific current context available to the user. */
  public readonly currentContext: store.selectors.BoundSelector<
    ents.core.admin.Context | undefined
  >

  /** The most specific current context with IDs and aliases. */
  public readonly currentContextWithAliases: store.selectors.BoundSelector<
    CurrentContextWithAliases | undefined
  >

  /**
   * Retrieves the desired tenant using a {@link ents.core.common.FlexibleIdentifier}
   */
  public readonly getTenant
  /**
   * Retrieves the desired zone using a {@link ents.core.common.FlexibleIdentifier}
   */
  public readonly getZone
  /**
   * Retrieves the desired app using a {@link ents.core.common.FlexibleIdentifier}
   */
  public readonly getApp
  /**
   * Retrieves the desired scenario using a {@link ents.core.common.FlexibleIdentifier}
   */
  public readonly getScenario

  /**
   * Indicates the user is currently on the given app
   */
  public readonly isCurrentlyOnApp
  /**
   * Indicates the user is currently on the given app page
   */
  public readonly isCurrentlyOnAppPage

  /** Whether the API Access panel is reachable in the current zone. */
  public readonly canAccessApiKeys
  /** Whether the current user can mint API keys in the current zone. */
  public readonly canGenerateApiKey
  /** Whether the current user can create scenarios in the current app. */
  public readonly currentUserCanCreateScenario
  /** Whether the current user can manage users of the current tenant. */
  public readonly currentUserCanManageUsers
  /** Whether the current user can configure the current app's functionality. */
  public readonly currentUserCanConfigureApp
  /** Checks whether the current user can edit a scenario. */
  public readonly canEditScenario
  /** Checks whether the current user can delete a scenario. */
  public readonly canDeleteScenario

  /**
   * Constructs a URL that will navigate to the desired tenant
   */
  public readonly getUrlForTenant
  /**
   * Constructs a URL that will navigate to the desired zone
   */
  public readonly getUrlForZone
  /**
   * Constructs a URL that will navigate to the desired app
   */
  public readonly getUrlForApp
  /**
   * Constructs a URL that will navigate to the desired scenario
   */
  public readonly getUrlForScenario
  /**
   * Constructs a URL that will navigate to the desired app
   */
  public readonly getUrlForLink

  /**
   * Returns the URL for the home page of the current tenant & zone
   */
  public readonly urlForHome

  /**
   * Indicates the user is currently on the home page for the current tenant &
   * zone
   */
  public readonly isCurrentlyOnHome

  /**
   * In some cases, we hide the home page because it is not useful
   */
  public readonly canAccessHome
  /**
   * Determines whether a platform landing route should stay on the no-access
   * page or redirect to the best tenant/zone home route.
   */
  public readonly getPlatformLandingRouteDecision

  /** Agents available to the current user on the current page */
  public readonly currentAgents

  constructor(feature: Feature, meFeature: me.Feature, _router: router.Router) {
    /* ------------------------------ Core Contexts ----------------------------- */

    /**
     * Sorts the given items based on when they were last accessed
     */
    function sortByLastAccessTime<T extends ents.core.common.Aliased>(
      scope: models.ScopedAlias.Scope,
      items: readonly T[] | undefined,
      lastAccessTimesByAlias: Record<string, number | undefined>,
    ): readonly T[] {
      if (!items) {
        return []
      }
      return [...items].sort(
        // Descending order, with undefined values appearing last
        (a, b) =>
          (lastAccessTimesByAlias[models.ScopedAlias.from(scope, b.alias)] ??
            0) -
          (lastAccessTimesByAlias[models.ScopedAlias.from(scope, a.alias)] ??
            0),
      )
    }

    this.tenantAlias = _router.selectors.getRouteParam(RouteParam.TenantAlias)
    this.availableTenants = meFeature.selectors.currentUser.project(
      feature.localStorage.selectors.lastAccessTimesByAlias.asSelector,
      (user, lastAccessTimesByAlias) =>
        sortByLastAccessTime('tenant', user?.tenants, lastAccessTimesByAlias),
    )
    this.tenant = this.availableTenants.project(
      this.tenantAlias.asSelector,
      (tenants, tenantAlias) =>
        tenants.find(tenant => tenant.alias === tenantAlias),
    )
    this.tenantId = this.tenant.project(tenant => tenant?.id)
    this.tenantContext = this.tenantId.project(tenantId =>
      tenantId ? { tenantId } : undefined,
    )

    this.zoneAlias = _router.selectors.getRouteParam(RouteParam.ZoneAlias)
    this.availableZones = this.tenant.project(
      feature.localStorage.selectors.lastAccessTimesByAlias.asSelector,
      (tenant, lastAccessTimesByAlias) =>
        sortByLastAccessTime('zone', tenant?.zones, lastAccessTimesByAlias),
    )
    this.zone = this.zoneAlias.project(
      this.availableZones.asSelector,
      (zoneAlias, zones) => zones.find(zone => zone.alias === zoneAlias),
    )
    this.zoneId = this.zone.project(zone => zone?.id)
    this.zoneContext = this.zoneId.project(
      this.tenantContext.asSelector,
      (zoneId, tenantContext) =>
        zoneId && tenantContext ? { ...tenantContext, zoneId } : undefined,
    )

    this.appAlias = _router.selectors.getRouteParam(RouteParam.AppAlias)
    this.availableApps = this.zoneId.project(
      (state: object) => state,
      (zoneId, state) =>
        zoneId
          ? meFeature.zoneApps.selectors.getApps(zoneId).asSelector(state)
          : [],
    )
    this.app = this.appAlias.project(
      this.availableApps.asSelector,
      (appAlias, apps) => apps.find(app => app.alias === appAlias),
    )
    this.appId = this.app.project(app => app?.id)
    this.appContext = this.appId.project(
      this.zoneContext.asSelector,
      (appId, zoneContext) =>
        appId && zoneContext ? { ...zoneContext, appId } : undefined,
    )

    this.appPageAlias = _router.selectors.getRouteParam(RouteParam.AppPageAlias)
    this.availableAppPages = this.app.project(app => app?.pages ?? [])
    this.appPage = this.appPageAlias.project(
      this.availableAppPages.asSelector,
      (appPageAlias, appPages) =>
        appPages.find(appPage => appPage.alias === appPageAlias),
    )
    this.appPageId = this.appPage.project(appPage => appPage?.id)
    this.pageId = this.appPage.project(appPage => appPage?.pageId)

    this.scenarioAlias = _router.selectors.getQueryParam(
      QueryParam.ScenarioAlias,
    )
    this.availableScenarios = this.app.project(app => app?.scenarios ?? [])
    this.scenario = this.scenarioAlias.project(
      this.availableScenarios.asSelector,
      (scenarioAlias, scenarios) =>
        scenarios.find(scenario => scenario.alias === scenarioAlias),
    )
    this.scenarioId = this.scenario.project(scenario => scenario?.id)
    this.scenarioContext = this.scenarioId.project(
      this.appContext.asSelector,
      (scenarioId, appContext) =>
        scenarioId && appContext ? { ...appContext, scenarioId } : undefined,
    )
    this.currentContext = this.tenantContext.project(
      this.zoneContext.asSelector,
      this.appContext.asSelector,
      this.scenarioContext.asSelector,
      (tenantContext, zoneContext, appContext, scenarioContext) =>
        scenarioContext ?? appContext ?? zoneContext ?? tenantContext,
    )
    this.currentContextWithAliases = this.currentContext.project(
      this.tenantAlias.asSelector,
      this.zoneAlias.asSelector,
      this.appAlias.asSelector,
      this.scenarioAlias.asSelector,
      (context, tenantAlias, zoneAlias, appAlias, scenarioAlias) => {
        if (context?.tenantId === undefined || tenantAlias === undefined) {
          return undefined
        }

        return {
          tenant: {
            id: context.tenantId,
            alias: tenantAlias,
          },
          ...(context.zoneId === undefined || zoneAlias === undefined
            ? {}
            : {
                zone: {
                  id: context.zoneId,
                  alias: zoneAlias,
                },
              }),
          ...(context.appId === undefined || appAlias === undefined
            ? {}
            : {
                app: {
                  id: context.appId,
                  alias: appAlias,
                },
              }),
          ...(context.scenarioId === undefined || scenarioAlias === undefined
            ? {}
            : {
                scenario: {
                  id: context.scenarioId,
                  alias: scenarioAlias,
                },
              }),
        }
      },
    )

    /* ------------------------- Current State Checkers ------------------------- */

    this.canAccessHome = this.availableApps.project(apps => apps.length !== 1)
    this.isCurrentlyOnHome = this.tenantAlias.project(
      this.zoneAlias.asSelector,
      this.appAlias.asSelector,
      (tenantAlias, zoneAlias, appAlias) =>
        appAlias === undefined &&
        zoneAlias !== undefined &&
        tenantAlias !== undefined,
    )

    this.currentAgents = this.tenant.project(
      this.zone.asSelector,
      this.app.asSelector,
      this.appPage.asSelector,
      (tenant, zone, app, appPage) => {
        const tenantAgents = tenant?.agents ?? []
        const zoneAgents = zone?.agents ?? []
        const appAgents = app?.agents ?? []
        const all = [...tenantAgents, ...zoneAgents, ...appAgents]

        return all.filter(
          agent =>
            hasCanUse(agent.access) &&
            isActiveOnCurrentPage(agent.pageContext, appPage),
        )
      },
    )

    const _createIsCurrentlyOnSelectorMethod = ({
      currentAlias,
      currentId,
    }: {
      readonly currentAlias: store.selectors.BoundSelector<string | undefined>
      readonly currentId: store.selectors.BoundSelector<string | undefined>
    }) =>
      feature.createSelectorMethod(
        ({
          alias,
          id,
        }: ents.core.common.FlexibleIdentifier<string>): store.selectors.BoundSelector<boolean> => {
          if (id) {
            return currentId.project(_id => _id === id)
          }
          if (alias) {
            return currentAlias.project(_alias => _alias === alias)
          }
          // If we are not given an ID or alias, then we are definitely not on
          // the requested item
          return feature.createSelector(() =>
            store.selectors.Selector.always(false),
          )
        },
      )

    /** @see {@link isCurrentlyOnApp} */
    this.isCurrentlyOnApp = _createIsCurrentlyOnSelectorMethod({
      currentAlias: this.appAlias,
      currentId: this.appId,
    })

    /** @see {@link isCurrentlyOnAppPage} */
    this.isCurrentlyOnAppPage = _createIsCurrentlyOnSelectorMethod({
      currentAlias: this.appPageAlias,
      currentId: this.appPageId,
    })

    this.canAccessApiKeys = this.zone.project(
      meFeature.selectors.currentUser.asSelector,
      (zone, user) => models.AppAccess.canAccessApiKeys({ user, zone }),
    )
    this.canGenerateApiKey = this.zone.project(
      this.tenant.asSelector,
      meFeature.selectors.currentUser.asSelector,
      (zone, tenant, user) =>
        models.AppAccess.canGenerateApiKey({ tenant, user, zone }),
    )
    this.currentUserCanCreateScenario = this.app.project(
      this.tenant.asSelector,
      meFeature.selectors.currentUser.asSelector,
      (app, tenant, user) =>
        models.AppAccess.canCreateScenario({ app, tenant, user }),
    )
    this.currentUserCanManageUsers = this.app.project(
      this.tenant.asSelector,
      meFeature.selectors.currentUser.asSelector,
      (app, tenant, user) =>
        models.AppAccess.canManageUsers({ app, tenant, user }),
    )
    this.currentUserCanConfigureApp = this.app.project(
      this.tenant.asSelector,
      meFeature.selectors.currentUser.asSelector,
      (app, tenant, user) =>
        models.AppAccess.canConfigureApp({ app, tenant, user }),
    )
    this.canEditScenario = feature.createSelectorMethod(
      (scenario: ents.core.admin.Scenario | undefined) =>
        this.app.project(
          this.tenant.asSelector,
          meFeature.selectors.currentUser.asSelector,
          (app, tenant, user) =>
            models.AppAccess.canEditScenario({ app, scenario, tenant, user }),
        ),
    )
    this.canDeleteScenario = feature.createSelectorMethod(
      (scenario: ents.core.admin.Scenario | undefined) =>
        this.app.project(
          this.tenant.asSelector,
          meFeature.selectors.currentUser.asSelector,
          (app, tenant, user) =>
            models.AppAccess.canDeleteScenario({
              app,
              scenario,
              tenant,
              user,
            }),
        ),
    )

    /* --------------------------------- Getters -------------------------------- */

    function _createGetter<
      TIdentifiers extends Partial<
        Record<string, ents.core.common.FlexibleIdentifier<string>>
      >,
      T extends ents.core.common.Identified<string> & ents.core.common.Aliased,
    >(
      type: keyof TIdentifiers,
      _default: store.selectors.BoundSelector<T | undefined>,
      available: (
        identifiers: TIdentifiers,
      ) => store.selectors.BoundSelector<readonly T[]>,
    ): (
      identifiers: TIdentifiers,
    ) => store.selectors.BoundSelector<T | undefined> {
      return feature.createSelectorMethod(identifiers => {
        const identifierForThisItem = identifiers[type]
        if (!identifierForThisItem) {
          return _default
        }
        const { id, alias } = identifierForThisItem
        if (id) {
          return available(identifiers).project(items =>
            items.find(item => item.id === id),
          )
        }
        if (alias) {
          return available(identifiers).project(items =>
            items.find(item => item.alias === alias),
          )
        }
        // If an identifier is retrieved, but has neither id nor alias, we
        // assume that an id or alias was meant to be passed but for some reason
        // is not defined. Therefore, it is more appropriate to say that there
        // was no match rather than use the default
        return feature.createSelector(() =>
          store.selectors.Selector.always(undefined),
        )
      })
    }
    this.getTenant = _createGetter(
      'tenant',
      this.tenant,
      (identifiers: TenantIdentifiers) => this.availableTenants,
    )
    this.getZone = _createGetter(
      'zone',
      this.zone,
      (identifiers: ZoneIdentifiers) =>
        this.getTenant(identifiers).project(tenant => tenant?.zones ?? []),
    )
    this.getApp = _createGetter(
      'app',
      this.app,
      (identifiers: AppIdentifiers) =>
        this.getZone(identifiers).project(
          (state: object) => state,
          (zone, state) =>
            zone
              ? meFeature.zoneApps.selectors.getApps(zone.id).asSelector(state)
              : [],
        ),
    )
    this.getScenario = _createGetter(
      'scenario',
      this.scenario,
      (identifiers: ScenarioIdentifiers) =>
        this.getApp(identifiers).project(app => app?.scenarios ?? []),
    )

    /* ---------------------------------- URLs ---------------------------------- */

    this.getPlatformLandingRouteDecision = feature.createSelectorMethod(
      (tenantAlias?: string | null) =>
        meFeature.state.project(
          feature.localStorage.selectors.lastAccessTimesByAlias.asSelector,
          (meState, lastAccessTimesByAlias): PlatformLandingRouteDecision => {
            // Auth can mount the platform before tenant/zone access has loaded.
            // Treat that as "not decided yet", not as genuine no-access.
            if (meState.user?.isLoading) {
              return { isSettled: false }
            }

            // This selector owns the fallback route choice. Root landing uses
            // the best remembered tenant; tenant landing stays within the
            // requested tenant and chooses its best remembered zone.
            const tenants = sortByLastAccessTime(
              'tenant',
              meState.user?.data?.tenants,
              lastAccessTimesByAlias,
            )
            const tenant = tenantAlias
              ? tenants.find(_tenant => _tenant.alias === tenantAlias)
              : tenants[0]
            const zone = sortByLastAccessTime(
              'zone',
              tenant?.zones,
              lastAccessTimesByAlias,
            )[0]

            return {
              isSettled: true,
              // No URL after settling means there is no tenant+zone home to
              // redirect to, so the no-access landing really is the page to
              // show.
              url:
                tenant && zone
                  ? _router.ngRouter.createUrlTree([
                      '/portal',
                      tenant.alias,
                      zone.alias,
                    ])
                  : undefined,
            }
          },
        ),
    )

    this.urlForHome = this.tenantAlias.project(
      this.zoneAlias.asSelector,
      (tenantAlias, zoneAlias) =>
        tenantAlias && zoneAlias
          ? `/portal/${tenantAlias}/${zoneAlias}`
          : undefined,
    )

    /** @see {@link getUrlForTenant} */
    this.getUrlForTenant = feature.createSelectorMethod(
      (identifiers: TenantIdentifiers) =>
        this.getTenant(identifiers).project(_tenant => {
          if (!_tenant) {
            // If we couldn't find the tenant, we can't generate a URL
            return undefined
          }
          if (_tenant.zones.length === 0) {
            // If the tenant does not have zones, we go to the tenant landing
            // page
            return _router.ngRouter.createUrlTree(['/portal', _tenant.alias])
          }
          // Otherwise, we go the first zone's page
          const zone = _tenant.zones[0]
          return _router.ngRouter.createUrlTree([
            '/portal',
            _tenant.alias,
            zone.alias,
          ])
        }),
    )
    /** @see {@link getUrlForZone} */
    this.getUrlForZone = feature.createSelectorMethod(
      (identifiers: ZoneIdentifiers) =>
        this.getZone(identifiers).project(
          this.getTenant(identifiers).asSelector,
          (zone, tenant) => {
            if (!zone || !tenant) {
              // If we couldn't find the zone or tenant, we cannot
              // generate a URL
              return undefined
            }
            return _router.ngRouter.createUrlTree([
              '/portal',
              tenant.alias,
              zone.alias,
            ])
          },
        ),
    )
    /** @see {@link getUrlForApp} */
    this.getUrlForApp = feature.createSelectorMethod(
      (identifiers: AppIdentifiers) =>
        this.getApp(identifiers).project(
          this.getZone(identifiers).asSelector,
          this.getTenant(identifiers).asSelector,
          (app, zone, tenant) => {
            if (!app || !zone || !tenant) {
              // If we couldn't find the app, zone, or tenant, we cannot
              // generate a URL
              return undefined
            }
            // App routes are route-bound provider boundaries. Navigating only
            // to /portal/:tenant/:zone/:app can briefly resolve an incomplete
            // route, so include the app's best landing page when we can.
            const defaultPageAlias = getDefaultAppPageAlias(app)
            const path = ['/portal', tenant.alias, zone.alias, app.alias]

            if (defaultPageAlias) {
              path.push(defaultPageAlias)
            }

            return _router.ngRouter.createUrlTree(path, {
              // Moving between apps must not blindly preserve the current
              // scenario. Each app owns its own scenario set, and apps without
              // scenarios need the query param cleared.
              queryParams: getScenarioQueryParamsForTargetApp(app),
            })
          },
        ),
    )

    /** @see {@link getUrlForScenario} */
    this.getUrlForScenario = feature.createSelectorMethod(
      (identifiers: ScenarioIdentifiers) =>
        this.getScenario(identifiers).project(
          (state: object) => state,
          (scenario, state) =>
            scenario === undefined
              ? undefined
              : _router.selectors
                  .getUrlWith({
                    // FIXME: get path for correct app & page
                    queryParams: {
                      plain: {
                        [QueryParam.ScenarioAlias]: scenario.alias,
                      },
                    },
                  })
                  .asSelector(state),
        ),
    )

    /** @see {@link getUrlForLink} */
    this.getUrlForLink = feature.createSelectorMethod(
      (
        { goto }: ents.core.display.Link,
        { app }: { app?: ents.core.common.FlexibleIdentifier<string> } = {},
      ) => {
        if (!goto) {
          // Without a destination, we can never generate a URL
          return store.selectors.Selector.always(undefined)
        }
        const { navigationId, segmentId, variables } = goto

        // Resolve the target app either from explicit context or from the
        // current route. We need the full app, not only the alias, so scenario
        // query params can be generated for the destination app instead of
        // blindly preserving the current app's scenario.
        const appBoundSelector = app ? this.getApp({ app }) : this.app

        return this.tenantAlias.project(
          this.zoneAlias.asSelector,
          appBoundSelector.asSelector,
          this.appAlias.asSelector,
          (state: object) => state,
          (tenantAlias, zoneAlias, targetApp, currentAppAlias, state) => {
            if (
              targetApp === undefined ||
              zoneAlias === undefined ||
              tenantAlias === undefined
            ) {
              // If we couldn't find the target app, current tenant alias, or
              // current zone alias, we cannot generate a URL.
              return undefined
            }
            // Links with an explicit navigationId already point at a concrete
            // page. App-level links need a default page so route-bound app
            // providers are created against the same complete route shape.
            const path = navigationId
              ? [
                  '/portal',
                  tenantAlias,
                  zoneAlias,
                  targetApp.alias,
                  navigationId,
                ]
              : [
                  '/portal',
                  tenantAlias,
                  zoneAlias,
                  targetApp.alias,
                  getDefaultAppPageAlias(targetApp),
                ].filter(TypeChecker.isNotNullish.raw)
            // Same-app links keep the active scenario untouched. Cross-app
            // links choose a valid default scenario for the target app, or
            // clear the query param when the target app has no scenarios.
            const scenarioQueryParams =
              targetApp.alias === currentAppAlias
                ? undefined
                : getScenarioQueryParamsForTargetApp(targetApp)

            return _router.selectors
              .getUrlWith({
                fragment: segmentId,
                path,
                queryParams: {
                  encoded: [
                    {
                      scope: variableOverrides.models.LinkValues.SCOPE,
                      value: variables,
                    },
                  ],
                  ...(scenarioQueryParams
                    ? { plain: scenarioQueryParams }
                    : {}),
                },
              })
              .asSelector(state)
          },
        )
      },
    )
  }
}

type TenantIdentifiers = FlexibleIdentifiers<'tenant'>
type ZoneIdentifiers = FlexibleIdentifiers<'tenant' | 'zone'>
type AppIdentifiers = FlexibleIdentifiers<'tenant' | 'zone' | 'app'>
type ScenarioIdentifiers = FlexibleIdentifiers<
  'tenant' | 'zone' | 'app' | 'scenario'
>

interface CurrentContextWithAliases {
  readonly tenant: Pick<ents.core.admin.Tenant, 'alias' | 'id'>
  readonly zone?: Pick<ents.core.admin.Zone, 'alias' | 'id'>
  readonly app?: Pick<ents.core.admin.App, 'alias' | 'id'>
  readonly scenario?: Pick<ents.core.admin.Scenario, 'alias' | 'id'>
}

type FlexibleIdentifiers<
  K extends string,
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
> = {
  /**
   * Defaults to current
   */
  readonly [_K in K]?: ents.core.common.FlexibleIdentifier<string>
}

interface PlatformLandingRouteDecision {
  readonly isSettled: boolean
  readonly url?: ngRouter.UrlTree
}

/**
 * Check CanUse bit (bit 0) in the access flags.
 */
function hasCanUse(access: number): boolean {
  // eslint-disable-next-line no-bitwise
  return (access & 1) !== 0
}

/**
 * Check if an agent is active on the current page based on its pageContext.
 * - No pageContext → always active (scope-level agent)
 * - pageAliases → active only on matching page aliases
 * - isForNonPrebuiltPages → active on all non-prebuilt (Mosaic/custom) pages
 */
function isActiveOnCurrentPage(
  pageContext: ents.core.agent.ZoneConfig.PageContext | undefined,
  appPage: ents.core.admin.App.Page | undefined,
): boolean {
  if (!pageContext) {
    return true
  }

  const { pageAliases, isForNonPrebuiltPages } = pageContext

  if (isForNonPrebuiltPages) {
    return appPage ? !ents.extra.prebuilt.Page.Id.isId(appPage.pageId) : false
  }

  if (pageAliases?.length) {
    return appPage ? pageAliases.includes(appPage.alias) : false
  }

  return true
}

function getDefaultAppPageAlias(
  app: ents.core.admin.App.ForUser,
): string | undefined {
  // Clone before sorting because app pages come from current-user state and
  // selector helpers must not mutate state-owned arrays.
  return [...app.pages].sort(compareAppPageDisplayOrder)[0]?.alias
}

function compareAppPageDisplayOrder(
  a: ents.core.admin.App.Page,
  b: ents.core.admin.App.Page,
): number {
  // Displayed pages are navigable from the app shell and should be preferred
  // over utility/internal pages when generating a complete app URL.
  if (!a.display) {
    return b.display ? 1 : 0
  }
  if (!b.display) {
    return -1
  }

  // Main pages are the natural landing target inside an app.
  const isMainDiff = (b.display.isMain ? 1 : 0) - (a.display.isMain ? 1 : 0)
  if (isMainDiff) {
    return isMainDiff
  }

  // Position resolves ties among pages with the same display/main status.
  return (
    (a.display?.position ?? Number.MAX_SAFE_INTEGER) -
    (b.display?.position ?? Number.MAX_SAFE_INTEGER)
  )
}

function getScenarioQueryParamsForTargetApp(
  app: ents.core.admin.App.ForUser,
): Record<string, string | null> {
  return {
    // A null value intentionally removes the stale scenario query param from
    // router-generated URLs when the target app does not support scenarios.
    [QueryParam.ScenarioAlias]: app.supportsScenarios
      ? // For scenario-capable apps, pick the first available scenario so the
        // destination route has a scenario that belongs to that app.
        (app.scenarios[0]?.alias ?? null)
      : null,
  }
}
